/**
 * Fetches available shipping options for the items currently in the Shopify cart.
 * Uses the Shopify AJAX API endpoint: /cart/shipping_rates.json
 *
 * NOTE: Shipping rates depend on the destination address.
 *
 * @param {Object} shippingAddress - The address to calculate rates for.
 * @param {string} shippingAddress.country - ISO 3166-1 alpha-2 country code or country name (e.g., 'United States' or 'US').
 * @param {string} shippingAddress.province - Province/State name (e.g., 'California') or code.
 * @param {string} shippingAddress.zip - Zip/Postal code.
 * @returns {Promise<Object>} - The JSON response containing shipping rates.
 */
async function getCartShippingRates(shippingAddress) {
    const { country, province, zip } = shippingAddress;

    // Build the query parameters
    const params = new URLSearchParams({
        'shipping_address[country]': country,
        'shipping_address[province]': province || '',
        'shipping_address[zip]': zip
    });

    try {
        const response = await fetch(`/cart/shipping_rates.json?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.status}`);
        }

        const data = await response.json();
        displayShippingRates(data.shipping_rates);
        return data.shipping_rates;

    } catch (error) {
        console.error('Error fetching shipping rates:', error);
        return { error: error.message };
    }
}

/**
 * Validates inputs and triggers the rate fetch.
 * Can be connected to a form submit or button click.
 */
function calculateShipping() {
    // Example: grabbing values from input fields with IDs 'address_country', 'address_province', 'address_zip'
    // Ensure these elements exist in your HTML
    const countryEl = document.getElementById('address_country');
    const provinceEl = document.getElementById('address_province');
    const zipEl = document.getElementById('address_zip');

    const country = countryEl ? countryEl.value : 'United States';
    const province = provinceEl ? provinceEl.value : '';
    const zip = zipEl ? zipEl.value : '';

    if (!country || !zip) {
        console.warn('Country and Zip code are required to calculate shipping.');
        return;
    }

    getCartShippingRates({
        country: country,
        province: province,
        zip: zip
    });
}

/**
 * Helper to display rates in the console or DOM
 * @param {Array} rates
 */
function displayShippingRates(rates) {
    if (!rates || rates.length === 0) {
        return;
    }

    // --- Update Cart Drawer UI ---
    // Target the elements added to cart-drawer.liquid
    const container = document.getElementById('shipping-estimate-container');
    const titleEl = document.getElementById('shipping-estimate-title');
    const priceEl = document.getElementById('shipping-estimate-price');

    if (container && titleEl && priceEl) {
        // Use the first available rate as requested
        const firstRate = rates[0];

        if (firstRate) {
            titleEl.textContent = firstRate.name;
            // Format price: "$220.00"
            priceEl.textContent = `$${firstRate.price}`;

            // Reveal the container
            container.style.display = 'flex';
        }
    }
}

// --- Dynamic Usage Based on Location ---
// Define example addresses for the two zones
const EXAMPLE_ADDRESSES = {
    US: {
        country: 'US',
        province: 'NY',
        zip: '10001'
    },
    EU: {
        country: 'FR',
        province: '',
        zip: '75001'
    }
};

/**
 * Detects the visitor's location.
 * First tries ipapi.co API for real-time IP geolocation (works with VPN).
 * Falls back to Shopify cookies if API fails.
 * Returns object with country and continent codes.
 * Defaults to US/NA on failure.
 */
async function detectVisitorLocation() {
    let detectedCountry = 'US';
    let detectedContinent = 'NA';
    let sourceUsed = 'default';

    // ========== METHOD 1: Real-time IP Geolocation API ==========
    try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
            const data = await response.json();

            if (data.country_code) {
                detectedCountry = data.country_code;
                sourceUsed = 'ipapi.co API';

                // Map continent code
                if (data.continent_code) {
                    detectedContinent = data.continent_code;
                }
            }
        }
    } catch (error) {
        // Fall through to next method
    }

    // ========== METHOD 2: Shopify Cookies (fallback) ==========
    if (sourceUsed === 'default') {
        const allCookies = document.cookie.split(';').reduce((acc, cookie) => {
            const [key, value] = cookie.trim().split('=');
            acc[key] = value;
            return acc;
        }, {});

        // Check localization cookie
        const localizationCookie = allCookies['localization'];
        if (localizationCookie) {
            try {
                const decoded = decodeURIComponent(localizationCookie);
                if (decoded.startsWith('{')) {
                    const locData = JSON.parse(decoded);
                    if (locData.country_code) {
                        detectedCountry = locData.country_code;
                        sourceUsed = 'localization cookie (JSON)';
                    }
                } else {
                    detectedCountry = decoded.toUpperCase();
                    sourceUsed = 'localization cookie (plain)';
                }
            } catch (e) {
                if (localizationCookie.length === 2) {
                    detectedCountry = localizationCookie.toUpperCase();
                    sourceUsed = 'localization cookie (fallback)';
                }
            }
        }

        // Check _shopify_sa_p cookie
        const shopifySaP = allCookies['_shopify_sa_p'];
        if (shopifySaP && sourceUsed === 'default') {
            const parts = shopifySaP.split('-');
            if (parts[0]) {
                detectedCountry = parts[0];
                sourceUsed = '_shopify_sa_p cookie';
            }
        }
    }

    // ========== METHOD 3: window.Shopify object (fallback) ==========
    if (sourceUsed === 'default' && window.Shopify && window.Shopify.Localization && window.Shopify.Localization.country) {
        detectedCountry = window.Shopify.Localization.country;
        sourceUsed = 'Shopify.Localization';
    }

    // ========== Determine continent from country ==========
    const euCountries = ['GB', 'FR', 'DE', 'IT', 'ES', 'PT', 'NL', 'BE', 'AT', 'IE', 'SE', 'DK', 'FI', 'GR', 'PL', 'CZ', 'HU', 'RO', 'BG', 'HR', 'SI', 'SK', 'LT', 'LV', 'EE', 'MT', 'CY', 'LU'];
    const naCountries = ['US', 'CA', 'MX'];
    const asCountries = ['JP', 'CN', 'KR', 'SG', 'HK', 'TW', 'MY', 'TH', 'VN', 'PH', 'ID'];
    const auCountries = ['AU', 'NZ'];

    if (detectedContinent === 'NA') {
        if (euCountries.includes(detectedCountry)) {
            detectedContinent = 'EU';
        } else if (naCountries.includes(detectedCountry)) {
            detectedContinent = 'NA';
        } else if (asCountries.includes(detectedCountry)) {
            detectedContinent = 'AS';
        } else if (auCountries.includes(detectedCountry)) {
            detectedContinent = 'OC';
        }
    }

    return {
        country: detectedCountry,
        continent: detectedContinent,
        source: sourceUsed
    };
}

/**
 * Initialize shipping based on visitor location.
 * This function is automatically called on page load,
 * but can also be manually called from other scripts.
 * @returns {Promise<void>}
 */
async function initShippingBasedOnLocation() {
    // Check if cart is empty before trying
    try {
        const cartResp = await fetch('/cart.js');
        const cartData = await cartResp.json();
        if (cartData.item_count === 0) {
            return;
        }
    } catch (e) {
        // Proceed anyway
    }

    const locationData = await detectVisitorLocation();
    let addressToUse;

    // IF Europe (Continent 'EU') -> Use Example EU Address
    // OTHERWISE -> Use Example US Address
    if (locationData.continent === 'EU') {
        addressToUse = EXAMPLE_ADDRESSES.EU;
    } else {
        addressToUse = EXAMPLE_ADDRESSES.US;
    }

    getCartShippingRates(addressToUse);
}

// Make functions globally accessible
window.initShippingBasedOnLocation = initShippingBasedOnLocation;
window.getCartShippingRates = getCartShippingRates;
window.detectVisitorLocation = detectVisitorLocation;

// Auto-execute on page load
initShippingBasedOnLocation();
